import { inject, Injectable, type Signal } from '@angular/core';
import { forkJoin, map, type Observable, of, tap } from 'rxjs';

// Models
import { type AccountStatus } from '../../models/AccountStatus';
import {
  type CursorPageRequest,
  type CursorPaginatedResponse,
} from '../../models/CursorPagination';
import { type DancerFilters, DancerModel } from '../../models/Dancer';
import { type JudgeFilters, JudgeModel } from '../../models/Judge';
import { type OrganizerFilters, OrganizerModel } from '../../models/Organizer';
import { type ParticipantModel } from '../../models/Participant';
import { type PoolModel } from '../../models/Pool';
import { ContestRole, type UserModel, type UserProfileDto } from '../../models/User';
import { type UserSort } from '../../models/UserSort';

// Services
import { ContextService } from './context.service';
import { UserApiService } from './user.api.service';

/**
 * Consumer service providing business logic for user management.
 * It relies on the ContextService for global session state and user identity.
 */
@Injectable({
  providedIn: 'root',
})
export class UserService {
  // =============================================================================
  // Public Properties (Source Data)
  // =============================================================================

  /** Reactive access to the current authenticated user identity. */
  get currentUser(): Signal<UserModel | null> {
    return this.contextService.currentUser;
  }

  /** Reactive access to the user's available participants. */
  get availableParticipants(): Signal<ParticipantModel[]> {
    return this.contextService.availableParticipants;
  }

  // =============================================================================
  // Private Properties
  // =============================================================================

  private readonly userApiService: UserApiService = inject(UserApiService);
  private readonly contextService: ContextService = inject(ContextService);

  // =============================================================================
  // Public Methods
  // =============================================================================

  /** Fetches a user by their unique identifier. */
  getUser(id: string): Observable<UserModel | DancerModel | JudgeModel> {
    return this.userApiService.getById(id);
  }

  /**
   * Refreshes the current user's profile and synchronizes the session context.
   */
  refreshMe(): Observable<UserModel> {
    return this.userApiService.me().pipe(
      map((userProfile: UserProfileDto) => {
        return {
          ...userProfile,
          firstName: userProfile.firstName ?? '',
          lastName: userProfile.lastName ?? '',
        } as UserModel;
      }),
      tap((user: UserModel) => {
        if (user) {
          this.contextService.updateUser(user);
        }
      }),
    );
  }

  /** Deletes a user account by ID. */
  deleteUser(id: string): Observable<Omit<UserModel | DancerModel | JudgeModel, 'id'>> {
    return this.userApiService.delete(id);
  }

  /** Batch deletes a list of dancers. */
  deleteDancerList(dancers: DancerModel[]): Observable<void[]> {
    if (dancers.length === 0) return of([]);
    const deleteRequests = dancers.map((dancer) =>
      this.userApiService.delete(dancer.id).pipe(map(() => void 0)),
    );
    return forkJoin(deleteRequests);
  }

  /** Batch deletes a list of judges. */
  deleteJudgeList(judges: JudgeModel[]): Observable<void[]> {
    if (judges.length === 0) return of([]);
    const deleteRequests = judges.map((judge) =>
      this.userApiService.delete(judge.id).pipe(map(() => void 0)),
    );
    return forkJoin(deleteRequests);
  }

  /** Batch deletes a list of organizers. */
  deleteOrganizerList(organizers: OrganizerModel[]): Observable<void[]> {
    if (organizers.length === 0) return of([]);
    const deleteRequests = organizers.map((organizer) =>
      this.userApiService.delete(organizer.id).pipe(map(() => void 0)),
    );
    return forkJoin(deleteRequests);
  }

  /** Fetches a list of users filtered by the Dancer role. */
  getDancerList(options?: { filters?: DancerFilters }): Observable<DancerModel[]> {
    return this.userApiService
      .getAll({
        filters: {
          ...options?.filters,
          contestRole: ContestRole.Dancer,
        },
      })
      .pipe(map((items) => items.map((item) => this.mapUserToParticipantModel(item, DancerModel))));
  }

  /** Fetches a list of users filtered by the Judge role. */
  getJudgeList(options?: { filters?: JudgeFilters }): Observable<JudgeModel[]> {
    return this.userApiService
      .getAll({
        filters: {
          ...options?.filters,
          contestRole: ContestRole.Judge,
        },
      })
      .pipe(map((items) => items.map((item) => this.mapUserToParticipantModel(item, JudgeModel))));
  }

  /** Fetches a list of users filtered by the Organizer role. */
  getOrganizerList(options?: { filters?: OrganizerFilters }): Observable<OrganizerModel[]> {
    return this.userApiService
      .getAll({
        filters: {
          ...options?.filters,
          contestRole: ContestRole.Organizer,
        },
      })
      .pipe(
        map((items) => items.map((item) => this.mapUserToParticipantModel(item, OrganizerModel))),
      );
  }

  /** General purpose user list retrieval with optional filtering. */
  getUserList(options?: {
    filters?: Record<string, string | number | boolean | null> | undefined;
  }): Observable<UserModel[]> {
    return this.userApiService
      .getAll({
        filters: options?.filters,
      })
      .pipe(map((response) => (Array.isArray(response) ? response : [])));
  }

  /** Cursor-paginated user list — the root /users endpoint is always unscoped (no per-contest variant). */
  getUserPage(options?: {
    filters?: Record<string, string | number | boolean | string[] | null>;
    sort?: UserSort;
    pagination?: CursorPageRequest;
  }): Observable<CursorPaginatedResponse<UserModel>> {
    return this.userApiService.getPage(options);
  }

  /** Finds a single user by their email address. */
  findUserByEmail(email: string): Observable<UserModel | null> {
    return this.getUserList({ filters: { email } }).pipe(
      map((users) => {
        const found = users.find((u) => u.email.toLowerCase() === email.toLowerCase());
        return found || null;
      }),
    );
  }

  /** Creates a new user account. */
  createUser(user: Omit<UserModel, 'id'>): Observable<UserModel> {
    return this.userApiService.post(user);
  }

  /** Updates an existing user's information. */
  updateUser(id: string, user: Partial<UserModel>): Observable<UserModel> {
    return this.userApiService.put(id, user).pipe(
      tap((updated) => {
        const current = this.contextService.currentUser();
        if (current && updated.id === current.id) {
          this.contextService.updateUser(updated);
        }
      }),
    );
  }

  /** Batch updates multiple users (limited fields like username, email). */
  updateUserList(userList: UserModel[]): Observable<UserModel[]> {
    const payload = {
      updates: userList.map((user) => ({
        id: user.id,
        data: {
          username: user.username,
          email: user.email,
        },
      })),
    };
    return this.userApiService.patch(payload);
  }

  /** Batch updates the account status of multiple users. */
  updateUserListStatus(users: UserModel[], status: AccountStatus): Observable<UserModel[]> {
    if (!users || users.length === 0) {
      return of([]);
    }
    const updateObservables = users.map((user) => this.updateAccountStatus(user.id, status));
    return forkJoin(updateObservables);
  }

  /** Updates the account status (enabled/disabled/unverified) of a user. */
  updateAccountStatus(id: string, status: AccountStatus): Observable<UserModel> {
    return this.userApiService.updateAccountStatus(id, status);
  }

  /** Updates account status specifically for a Judge participant model. */
  updateJudgeStatus(id: string, status: AccountStatus): Observable<JudgeModel> {
    return this.updateAccountStatus(id, status).pipe(
      map((user) => this.mapUserToParticipantModel(user, JudgeModel)),
    );
  }

  /** Updates account status specifically for a Dancer participant model. */
  updateDancerStatus(id: string, status: AccountStatus): Observable<DancerModel> {
    return this.updateAccountStatus(id, status).pipe(
      map((user) => this.mapUserToParticipantModel(user, DancerModel)),
    );
  }

  /** Updates account status specifically for an Organizer participant model. */
  updateOrganizerStatus(id: string, status: AccountStatus): Observable<OrganizerModel> {
    return this.updateAccountStatus(id, status).pipe(
      map((user) => this.mapUserToParticipantModel(user, OrganizerModel)),
    );
  }

  /** Creates a new Judge account. */
  createJudge(user: Omit<JudgeModel, 'id'>): Observable<JudgeModel> {
    const { status: _, ...userData } = user;
    const payload: Omit<UserModel, 'id'> = {
      ...userData,
      status: user.accountStatus,
      contestRole: ContestRole.Judge,
    } as Omit<UserModel, 'id'>;
    return this.userApiService
      .post(payload)
      .pipe(map((u) => this.mapUserToParticipantModel(u, JudgeModel)));
  }

  /** Updates an existing Judge's information. */
  updateJudge(id: string, judge: JudgeModel): Observable<JudgeModel> {
    const { status: _, ...updateData } = judge;
    const payload: Partial<UserModel> = {
      ...updateData,
      status: judge.accountStatus,
    };
    return this.updateUser(id, payload).pipe(
      map((user) => this.mapUserToParticipantModel(user, JudgeModel)),
    );
  }

  /** Batch updates multiple Judges. */
  updateJudgeList(judgeList: JudgeModel[]): Observable<JudgeModel[]> {
    const payload = {
      updates: judgeList.map((judge) => ({
        id: judge.id,
        data: {
          username: judge.username,
        },
      })),
    };
    return this.userApiService
      .patch(payload)
      .pipe(map((items) => items.map((item) => this.mapUserToParticipantModel(item, JudgeModel))));
  }

  /** Creates a new Dancer account. */
  createDancer(user: Omit<DancerModel, 'id'>): Observable<DancerModel> {
    const { status: _, ...userData } = user;
    const payload: Omit<UserModel, 'id'> = {
      ...userData,
      status: user.accountStatus,
      contestRole: ContestRole.Dancer,
    } as Omit<UserModel, 'id'>;
    return this.userApiService
      .post(payload)
      .pipe(map((u) => this.mapUserToParticipantModel(u, DancerModel)));
  }

  /** Updates an existing Dancer's information. */
  updateDancer(id: string, dancer: DancerModel): Observable<DancerModel> {
    const { status: _, ...updateData } = dancer;
    const payload: Partial<UserModel> = {
      ...updateData,
      status: dancer.accountStatus,
    };
    return this.updateUser(id, payload).pipe(
      map((user) => this.mapUserToParticipantModel(user, DancerModel)),
    );
  }

  /** Batch updates multiple Dancers. */
  updateDancerList(dancerList: DancerModel[]): Observable<DancerModel[]> {
    const payload = {
      updates: dancerList.map((dancer) => ({
        id: dancer.id,
        data: {
          username: dancer.username,
          bib: dancer.bib,
          dancerRole: dancer.dancerRole,
        },
      })),
    };
    return this.userApiService
      .patch(payload)
      .pipe(map((items) => items.map((item) => this.mapUserToParticipantModel(item, DancerModel))));
  }

  /** Creates a new Organizer account. */
  createOrganizer(user: Omit<UserModel, 'id'>): Observable<OrganizerModel> {
    return this.createUser(user).pipe(
      map((u) => this.mapUserToParticipantModel(u, OrganizerModel)),
    );
  }

  /** Updates an existing Organizer's information. */
  updateOrganizer(
    id: string,
    organizer: OrganizerModel | (Record<string, unknown> & { user?: UserModel }),
  ): Observable<OrganizerModel> {
    // If organizer is a Participant object, extract user data or flattened props
    const updateData = 'user' in organizer && organizer.user ? organizer.user : organizer;
    return this.updateUser(id, updateData as Partial<UserModel>).pipe(
      map((u) => this.mapUserToParticipantModel(u, OrganizerModel)),
    );
  }

  /** Batch updates multiple Organizers. */
  updateOrganizerList(organizerList: OrganizerModel[]): Observable<OrganizerModel[]> {
    const payload = {
      updates: organizerList.map((organizer) => ({
        id: organizer.id,
        data: {
          username: organizer.username,
        },
      })),
    };
    return this.userApiService
      .patch(payload)
      .pipe(
        map((items) => items.map((item) => this.mapUserToParticipantModel(item, OrganizerModel))),
      );
  }

  /** Fetches all participants for a specific user. */
  getUserParticipants(userId: string): Observable<ParticipantModel[]> {
    return this.userApiService.getUserParticipants(userId);
  }

  /** Connects a user to a contest by creating a participant entry. */
  addUserParticipant(contestId: string, data: Partial<ParticipantModel>): Observable<unknown> {
    return this.userApiService.addUserParticipant(contestId, data);
  }

  /** Removes a user from a contest. */
  removeUserParticipant(contestId: string, participantId: string): Observable<unknown> {
    return this.userApiService.removeUserParticipant(contestId, participantId);
  }

  /** Fetches the list of pools associated with a user. */
  getPoolListOfUser(userId: string): Observable<PoolModel[]> {
    return this.userApiService.getPoolListOfUser(userId);
  }

  // =============================================================================
  // Private Methods
  // =============================================================================

  /**
   * Maps a raw user-like object to a specific participant model class.
   * Handles the status vs accountStatus discrepancy.
   */
  private mapUserToParticipantModel<T extends ParticipantModel>(
    data: Omit<Partial<T>, 'status'> & { status?: AccountStatus },
    constructor: new () => T,
  ): T {
    const model = new constructor();
    const { status, ...rest } = data;
    Object.assign(model, rest);
    if (status) {
      model.accountStatus = status;
    }
    return model;
  }

  /**
   * Maps a raw object to a specific model class.
   */
  private mapToModel<T extends object>(data: Partial<T>, constructor: new () => T): T {
    const model = new constructor();
    Object.assign(model, data);
    return model;
  }

  /**
   * Removes undefined values from a filter object to avoid sending them in the API request.
   */
  private removeUndefined<T extends object>(obj: T): T {
    return Object.fromEntries(Object.entries(obj).filter(([_, value]) => value !== undefined)) as T;
  }
}
